import SKIES from "./skies.json";

export const SECTOR_LENGTH = 60;

const LAYOUTS = {
  garden: { amp: [3.0, 1.4], rows: [[-1, 3.6], [1, 3.6], [-1, 6.8], [1, 6.8]], steps: 6, turn: 0.45 },
  forest: { amp: [2.4, 1.2], rows: [[-1, 3.4], [1, 3.4], [-1, 6.4], [1, 6.4]], steps: 6, turn: 0.5 },
  shore: { amp: [1.6, 0.8], rows: [[-1, 3.6], [-1, 6.8], [-1, 10.0]], steps: 8, turn: 0.3 },
};

export const pathX = (env, z) => {
  const [a, b] = LAYOUTS[env].amp;
  return a * Math.sin(z * 0.05) + b * Math.sin(z * 0.13);
};

export const smoothstep = (a, b, x) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

export function heightAt(env, x, z) {
  const rel = x - pathX(env, z);
  if (env === "shore") {
    if (rel > 8) return -smoothstep(8, 20, rel) * 2.2;
    return smoothstep(16, 45, -rel) * (2.5 + Math.sin(z * 0.05) * 1.5 + Math.sin(x * 0.11) * 0.8);
  }
  const d = Math.abs(rel);
  const amp = env === "forest" ? 1.8 : 2.6;
  return smoothstep(13, 42, d) * (amp + Math.sin(x * 0.07) * Math.cos(z * 0.05) * amp * 0.8);
}

export function slotPositions(env, sector) {
  const lay = LAYOUTS[env];
  const spacing = (SECTOR_LENGTH - 12) / lay.steps;
  const out = [];
  for (let step = 0; step < lay.steps; step++) {
    lay.rows.forEach(([side, off], r) => {
      const rowInSide = lay.rows.slice(0, r).filter(([s]) => s === side).length;
      const z = -sector * SECTOR_LENGTH - 6 - step * spacing - (rowInSide % 2) * spacing * 0.5;
      out.push({ x: pathX(env, z) + side * off, z });
    });
  }
  return out;
}

export const sectorOfZ = (z) => Math.max(0, Math.floor(-z / SECTOR_LENGTH));

export function rng(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function hashString(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

const rotY = (v, t) => {
  const x = v[0] * Math.cos(t) + v[2] * Math.sin(t);
  const z = -v[0] * Math.sin(t) + v[2] * Math.cos(t);
  const l = Math.hypot(x, v[1], z);
  return [x / l, v[1] / l, z / l];
};

const SUN_AZ = { garden: -2.196, forest: -1.2, shore: -0.54 };
const BASE = {
  garden: { ground: ["#6c8743", "#86a052", "#a3aa62"], path: "#cdb48c", tex: { ground: "grass", groundTile: 3, path: "gravel", pathTile: 1.6 }, fogFar: 210 },
  forest: { ground: ["#465630", "#56663a", "#6e6644"], path: "#8d7a5c", tex: { ground: "forest_floor", groundTile: 3, path: "forest_path", pathTile: 2 }, fogFar: 115 },
  shore: { ground: ["#7a8752", "#97a063", "#dcc79c"], path: "#d6c19a", tex: { ground: "sand", groundTile: 3, path: "sand_path", pathTile: 2 }, fogFar: 300 },
};
const NIGHT = { sky: "qwantani_night_puresky", env: 0.12, bg: 1, light: ["#a9bcff", 0.45], hemi: ["#3d4c6e", "#0b0f14", 0.3], fog: "#151b27", tint: "#4e5a72", water: ["#0b1422", "#1e2c40", "#c9d6ff"] };
const PRESETS = {
  garden: {
    dawn: { sky: "qwantani_sunrise_puresky", env: 0.6, bg: 1, light: ["#ffc9a0", 1.6], hemi: ["#ffd9c0", "#4f5a3a", 0.3], fog: "#c0b3a6", tint: "#efd8c6" },
    day: { sky: "kloofendal_48d_partly_cloudy_puresky", env: 0.85, bg: 1, light: ["#fff1dc", 2.5], hemi: ["#e4ecf5", "#59683c", 0.35], fog: "#aeb5bd", tint: "#e9efe0" },
    sunset: { sky: "rosendal_park_sunset_puresky", env: 0.65, bg: 1, light: ["#ffb27a", 1.8], hemi: ["#f6c7b0", "#4a4d36", 0.3], fog: "#b6aebb", tint: "#efcdb6" },
    night: NIGHT,
  },
  forest: {
    dawn: { sky: "kloofendal_misty_morning_puresky", env: 0.75, bg: 0.95, light: ["#f5efe2", 1.5], hemi: ["#e6e9ec", "#2f3824", 0.35], fog: "#b9babd", tint: "#c3c9bd" },
    day: { sky: "qwantani_mid_morning_puresky", env: 0.8, bg: 1, light: ["#fff4e2", 1.9], hemi: ["#e2ebf3", "#33402a", 0.35], fog: "#b1bac3", tint: "#ccd3c4" },
    sunset: { sky: "qwantani_dusk_2_puresky", env: 0.55, bg: 1, light: ["#ffb48a", 1.2], hemi: ["#d9b7c0", "#2c2a24", 0.3], fog: "#9b8a97", tint: "#c2adab" },
    night: { ...NIGHT, fog: "#11161e", tint: "#3c4656" },
  },
  shore: {
    dawn: { sky: "qwantani_dawn_puresky", env: 0.7, bg: 1, light: ["#ffc59e", 1.8], hemi: ["#ffd8c8", "#4f5b66", 0.3], fog: "#c0b3ac", tint: "#f0d6c8", water: ["#2b3f58", "#7f93aa", "#ffc8a0"] },
    day: { sky: "qwantani_mid_morning_puresky", env: 0.9, bg: 1, light: ["#fff3e0", 2.5], hemi: ["#e3edf6", "#4f5b66", 0.35], fog: "#b4bec8", tint: "#f4ece0", water: ["#1d4a66", "#5f9bb5", "#ffffff"] },
    sunset: { sky: "qwantani_sunset_puresky", env: 0.85, bg: 1, light: ["#ffcf9c", 2.4], hemi: ["#ffe2c4", "#4f5b66", 0.35], fog: "#c9c0ae", tint: "#f3dfc6", water: ["#1f3a52", "#6d8ea3", "#ffc887"] },
    night: { ...NIGHT, fog: "#182030", tint: "#56627a" },
  },
};

export const PHASES = ["dawn", "day", "sunset", "night"];
export function phaseAt(date = new Date()) {
  const h = date.getHours() + date.getMinutes() / 60;
  if (h >= 5 && h < 8) return "dawn";
  if (h >= 8 && h < 17.5) return "day";
  if (h >= 17.5 && h < 20.5) return "sunset";
  return "night";
}

const cache = {};
export function preset(env, phase = "day") {
  const key = `${env}:${phase}`;
  if (cache[key]) return cache[key];
  const p = PRESETS[env][phase];
  const raw = SKIES[p.sky].sun;
  const night = phase === "night";
  const skyRot = Math.atan2(raw[2], raw[0]) - SUN_AZ[env] + (night ? Math.PI : 0);
  const d = night ? rotY([0.35, 0.8, -0.5], 0) : rotY(raw, skyRot);
  const lightDir = night ? d : [d[0], Math.max(d[1], 0.28), d[2]];
  const fogNear = env === "forest" ? 14 : 40;
  cache[key] = {
    ...BASE[env], phase, sky: p.sky, skyRot, envIntensity: p.env, bgIntensity: p.bg, tint: p.tint, water: p.water,
    fog: [p.fog, night ? fogNear * 0.6 : fogNear, night ? BASE[env].fogFar * 0.55 : BASE[env].fogFar],
    hemi: p.hemi, sun: d.map((v) => v * 100),
    light: { color: p.light[0], intensity: p.light[1], dir: lightDir.map((v) => v * 40) },
  };
  return cache[key];
}

export const ENV = Object.fromEntries(Object.keys(PRESETS).map((k) => [k, preset(k, "day")]));
